import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import type { MonitoringMapPoint } from '../../types/monitoring';
import {
  announcementTypeLabel,
  formatMonitoringDateTime,
  kindColor,
  kindLabel,
  resolveMonitoringImageUrl,
} from '../../utils/monitoringPresentation';

const MATCHING_COLOR = '#0ea5e9';

type Props = {
  point: MonitoringMapPoint;
  lostColor: string;
  foundColor: string;
};

const MonitoringMapPointCard: React.FC<Props> = ({ point, lostColor, foundColor }) => {
  const accent = kindColor(point.kind, lostColor, foundColor, MATCHING_COLOR);
  const imageUrl = resolveMonitoringImageUrl(point.image_url);
  const targetImageUrl = resolveMonitoringImageUrl(point.target_image_url);

  return (
    <Card
      elevation={8}
      sx={{
        width: 300,
        maxWidth: 'min(300px, 88vw)',
        borderRadius: 2,
        overflow: 'hidden',
        border: `2px solid ${accent}`,
        pointerEvents: 'none',
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={point.label}
          sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', bgcolor: '#f1f5f9' }}
        />
      ) : null}
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
          <Chip
            size="small"
            label={kindLabel(point.kind)}
            sx={{ bgcolor: accent, color: point.kind === 'lost' ? '#1e293b' : '#fff', fontWeight: 700, height: 24 }}
          />
          {point.announcement_type ? (
            <Chip
              size="small"
              variant="outlined"
              label={announcementTypeLabel(point.announcement_type)}
              sx={{ height: 24, fontWeight: 600 }}
            />
          ) : null}
          {point.category_name ? (
            <Chip size="small" variant="outlined" label={point.category_name} sx={{ height: 24 }} />
          ) : null}
        </Stack>

        <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
          {point.label}
        </Typography>

        {point.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
            {point.description}
          </Typography>
        ) : null}

        {point.city ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {point.city}
          </Typography>
        ) : null}

        {point.created_at ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatMonitoringDateTime(point.created_at)}
          </Typography>
        ) : null}

        {point.kind === 'ga4' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.45 }}>
            {point.active_users === 1
              ? '1 utilisateur actif sur sherlook.fr (≈ 30 min)'
              : `${point.active_users ?? 0} utilisateurs actifs sur sherlook.fr (≈ 30 min)`}
          </Typography>
        ) : null}

        {point.kind === 'matching' ? (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: MATCHING_COLOR }}>
              Matching · {point.match_score != null ? `${Math.round(point.match_score)} %` : '—'}
              {point.status ? ` · ${point.status}` : ''}
            </Typography>
            {point.match_reason ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                {point.match_reason}
              </Typography>
            ) : null}
            {point.target_title ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                {targetImageUrl ? (
                  <Box
                    component="img"
                    src={targetImageUrl}
                    alt={point.target_title}
                    sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : null}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Annonce liée ({announcementTypeLabel(point.target_type)})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {point.target_title}
                  </Typography>
                </Box>
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default MonitoringMapPointCard;
